import { BaseResource } from './base';
import { Student, ListResponse, PaginationParams } from '../types';

export interface CreateStudentData {
    firstName: string;
    lastName: string;
    email: string;
    grade: string;
    dateOfBirth: string;
    enrollmentDate?: string;
    address: {
        street: string;
        city: string;
        state: string;
        zipCode: string;
        country?: string;
    };
    parentContact: {
        name: string;
        email: string;
        phone: string;
        relationship?: string;
    };
    emergencyContacts?: Array<{
        name: string;
        email: string;
        phone: string;
        relationship?: string;
    }>;
    medicalInfo?: {
        allergies?: string[];
        medications?: string[];
        conditions?: string[];
    };
}

export interface UpdateStudentData extends Partial<CreateStudentData> {
    status?: 'active' | 'inactive' | 'graduated';
}

export interface StudentFilters extends PaginationParams {
    grade?: string;
    status?: 'active' | 'inactive' | 'graduated';
    search?: string;
    enrollmentDateFrom?: string;
    enrollmentDateTo?: string;
}

export class StudentsResource extends BaseResource {
    /**
     * List all students with optional filtering and pagination
     */
    async list(filters?: StudentFilters): Promise<ListResponse<Student>> {
        return super.requestGet<ListResponse<Student>>('/students', filters);
    }

    /**
     * Get a specific student by ID
     */
    async get(studentId: string): Promise<Student> {
        return super.requestGet<Student>(`/students/${studentId}`);
    }

    /**
     * Create a new student
     */
    async create(data: CreateStudentData): Promise<Student> {
        return super.requestPost<Student>('/students', data);
    }

    /**
     * Update an existing student
     */
    async update(studentId: string, data: UpdateStudentData): Promise<Student> {
        return super.requestPatch<Student>(`/students/${studentId}`, data);
    }

    /**
     * Delete a student
     */
    async delete(studentId: string): Promise<{ success: boolean; message: string }> {
        return super.requestDelete(`/students/${studentId}`);
    }

    /**
     * Get student's enrollment history
     */
    async getEnrollments(studentId: string, filters?: PaginationParams): Promise<ListResponse<any>> {
        return super.requestGet<ListResponse<any>>(`/students/${studentId}/enrollments`, filters);
    }

    /**
     * Get student's grades
     */
    async getGrades(studentId: string, filters?: PaginationParams & { courseId?: string; semester?: string }): Promise<ListResponse<any>> {
        return super.requestGet<ListResponse<any>>(`/students/${studentId}/grades`, filters);
    }

    /**
     * Get student's attendance records
     */
    async getAttendance(studentId: string, filters?: PaginationParams & { courseId?: string; dateFrom?: string; dateTo?: string }): Promise<ListResponse<any>> {
        return super.requestGet<ListResponse<any>>(`/students/${studentId}/attendance`, filters);
    }

    /**
     * Bulk create students
     */
    async bulkCreate(students: CreateStudentData[]): Promise<{ created: Student[]; errors: any[] }> {
        return super.requestPost('/students/bulk', { students });
    }

    /**
     * Search students by name or email
     */
    async search(query: string, filters?: Omit<StudentFilters, 'search'>): Promise<ListResponse<Student>> {
        return super.requestGet<ListResponse<Student>>('/students/search', { q: query, ...filters });
    }
}
